import WidgetBase from '../../common/widgetBase';
import SystemOfWorkClient from '../domain/client';
import '../missionControl.css';

/**
 * WorkNodeBase — shared base for the System of Work playground widgets
 * (Missions, Cell Workflows, Workspaces).
 *
 * Each of the three widgets is a thin React node that:
 *   - constructs ONE SystemOfWorkClient from the playground session context, and
 *   - renders a self-contained surface from the shared `surfaces/` library.
 *
 * This base factors out the bits that would otherwise be copy-pasted three times:
 * the api/revision/session accessors and the client construction. Sizing is NOT
 * overridden — WidgetBase.getEffectiveSize applies, so a grid playground fills the
 * cell while a free-form one honours the widget's width/height ports (the surface
 * itself is full-bleed within whatever box that yields, see missionControl.css).
 * It deliberately does NOT own a mission store, reload loop, or
 * applyLocal — only the Missions widget needs those, so they live in
 * `missions/index.js`. The Cell Workflows / Workspaces widgets are thin: build a
 * client, render their own self-loading surface.
 *
 * The client is built lazily (see getClient) so it is ready on the FIRST render —
 * `this.context` is populated before the initial render, so a self-loading surface
 * mounts with a valid client and can fetch immediately.
 */
export default class WorkNodeBase extends WidgetBase {
  constructor(props) {
    super(props);
    this._client = null;
    this._mounted = false;
  }

  // ---------------------------------------------------------------------------
  // Accessors — mirror the graph widget pattern
  // ---------------------------------------------------------------------------

  _getApi() {
    return this.props.data.shellInterface.getApp().api;
  }

  _getRevisionId() {
    return this.props.data.shellInterface.getRevision().id;
  }

  _getSessionId() {
    return this.context && this.context.session ? this.context.session.id : null;
  }

  _buildClient() {
    return new SystemOfWorkClient({
      api: this._getApi(),
      sessionManager: this.context,
      revisionId: this._getRevisionId(),
      getSessionId: () => this._getSessionId(),
    });
  }

  /**
   * Lazily build and cache the SystemOfWorkClient. `this.context` (the
   * SessionManager) is available from the first render onward, so the client is
   * ready before any child surface mounts. The SessionManager instance is stable
   * for the widget's lifetime, so a single cached client is correct.
   * @returns {SystemOfWorkClient}
   */
  getClient() {
    if (!this._client) this._client = this._buildClient();
    return this._client;
  }

  /**
   * A stable remount key for a self-loading surface bound to one session object
   * (by alias). It changes when the session is switched/selected, and once when
   * the bound object's value first becomes available — so the surface re-runs its
   * mount-time fetch against live data. It does NOT change on every unrelated
   * session-content notification, which would remount the surface mid-edit and
   * discard in-progress (unsaved) work.
   * @param {string} alias
   * @returns {string}
   */
  surfaceKey(alias) {
    const sessionId = this._getSessionId() || 'none';
    const hasValue = !!(
      this.context &&
      this.context.getSessionData &&
      this.context.getSessionData(alias) !== undefined
    );
    return `${sessionId}:${hasValue ? 'ready' : 'pending'}`;
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  componentDidMount() {
    super.componentDidMount();
    this._mounted = true;
    // Ensure the client exists post-mount even if a subclass's first render did
    // not happen to call getClient().
    this.getClient();
  }

  componentWillUnmount() {
    this._mounted = false;
    super.componentWillUnmount();
  }

}
